/**
 * =============================================================================
 * contentProtection.ts - Livelli di protezione contenuti per tenant
 * =============================================================================
 *
 * Definisce i livelli di protezione contenuti selezionabili da ciascun tenant
 * via /admin/protezione-contenuti. Il livello scelto viene persistito in
 * `tenants.config.content_protection.level` e applicato a runtime dal
 * componente <ContentProtectionGate> tramite l'hook useContentProtection.
 *
 * IMPORTANTE — limiti tecnici reali:
 * - Nessuna protezione client-side è assoluta. Un utente determinato con
 *   accesso al browser può sempre catturare la pagina (screenshot OS, foto
 *   del monitor, DevTools, ecc.). Le protezioni qui sotto sono DETERRENTI
 *   che alzano l'attrito e rendono esplicita la volontà del titolare di
 *   non autorizzare copia/diffusione.
 * - L'unico vero blocco è quello server-side: autenticazione + RLS, che è
 *   già attivo per tutti i contenuti riservati.
 *
 * @version 1.0.0 - Fase 1 (L0–L3, protezioni "soft")
 * =============================================================================
 */

/** Identificatori dei livelli supportati nella Fase 1. */
export type ContentProtectionLevel = "L0" | "L1" | "L2" | "L3";

/**
 * Flag effettivi calcolati a partire dal livello selezionato.
 * Il componente <ContentProtectionGate> li usa per applicare le protezioni.
 */
export interface ContentProtectionFlags {
  /** Disabilita il menu contestuale (tasto destro). */
  disableContextMenu: boolean;
  /** Disabilita la selezione del testo (user-select: none). */
  disableTextSelection: boolean;
  /** Intercetta l'evento `copy` e gli shortcut Ctrl/Cmd+C. */
  disableCopy: boolean;
  /** Blocca il trascinamento delle immagini. */
  disableDrag: boolean;
  /** Disabilita la stampa via CSS @media print + intercetta Ctrl/Cmd+P. */
  disablePrint: boolean;
  /** Intercetta Ctrl/Cmd+S (salva pagina). */
  disableSave: boolean;
  /** Nasconde i pulsanti di esportazione contenuti (DOCX/PDF/MD). */
  disableExportCourse: boolean;
}

/** Configurazione persistita in `tenants.config.content_protection`. */
export interface ContentProtectionConfig {
  level: ContentProtectionLevel;
  /** Testo informativo mostrato nel footer / overlay (opzionale). */
  legal_notice?: string | null;
}

/** Preset per ciascun livello. Nessun override granulare in Fase 1. */
export const LEVEL_PRESETS: Record<ContentProtectionLevel, ContentProtectionFlags> = {
  L0: {
    disableContextMenu: false,
    disableTextSelection: false,
    disableCopy: false,
    disableDrag: false,
    disablePrint: false,
    disableSave: false,
    disableExportCourse: false,
  },
  L1: {
    // L1 = solo autenticazione + RLS (già garantito server-side).
    // Nessuna protezione client aggiuntiva.
    disableContextMenu: false,
    disableTextSelection: false,
    disableCopy: false,
    disableDrag: false,
    disablePrint: false,
    disableSave: false,
    disableExportCourse: false,
  },
  L2: {
    // L2 = blocco esportazione e stampa, ma il testo resta selezionabile/copiabile.
    disableContextMenu: false,
    disableTextSelection: false,
    disableCopy: false,
    disableDrag: false,
    disablePrint: true,
    disableSave: true,
    disableExportCourse: true,
  },
  L3: {
    // L3 = anti-copia "soft" massima possibile lato client.
    disableContextMenu: true,
    disableTextSelection: true,
    disableCopy: true,
    disableDrag: true,
    disablePrint: true,
    disableSave: true,
    disableExportCourse: true,
  },
};

/** Metadati human-readable per la UI admin. */
export interface LevelMeta {
  label: string;
  short: string;
  description: string;
  /** Cosa viene impedito (deterrenti attivi). */
  prevents: string[];
  /** Cosa resta tecnicamente possibile. */
  remains: string[];
}

export const LEVEL_META: Record<ContentProtectionLevel, LevelMeta> = {
  L0: {
    label: "L0 — Aperto",
    short: "Nessuna protezione",
    description:
      "Tutti i contenuti sono pubblici e liberamente accessibili, copiabili, stampabili ed esportabili. Adatto a portali divulgativi o a materiali rilasciati in open access.",
    prevents: ["Nulla viene impedito"],
    remains: [
      "Accesso anonimo a tutti i contenuti",
      "Selezione, copia e stampa libere",
      "Esportazione di corsi e pagine in DOCX/PDF",
      "Indicizzazione completa da parte dei motori di ricerca",
    ],
  },
  L1: {
    label: "L1 — Autenticato",
    short: "Solo utenti registrati",
    description:
      "I contenuti riservati sono accessibili solo agli utenti autenticati, secondo i permessi del proprio ruolo. Nessuna protezione attiva contro copia, stampa o esportazione.",
    prevents: [
      "Accesso anonimo ai contenuti riservati",
      "Visualizzazione di contenuti per cui non si è autorizzati",
    ],
    remains: [
      "Copia del testo selezionato",
      "Stampa delle pagine",
      "Esportazione di corsi in DOCX/PDF (se il ruolo lo consente)",
      "Salvataggio della pagina via browser",
    ],
  },
  L2: {
    label: "L2 — No esportazione / stampa",
    short: "Blocco esportazione e stampa",
    description:
      "Oltre all'autenticazione, vengono disabilitati i pulsanti di esportazione e la stampa diretta della pagina. Il testo resta selezionabile per consentire una lettura agevole, ma il portale non offre più alcun percorso ufficiale per portare i contenuti fuori dal browser.",
    prevents: [
      "Accesso anonimo (come L1)",
      "Esportazione dei contenuti in DOCX, PDF o Markdown (pulsanti nascosti)",
      "Stampa diretta dal browser (Ctrl/Cmd+P intercettato, layout di stampa neutralizzato)",
      "Salvataggio della pagina con Ctrl/Cmd+S",
    ],
    remains: [
      "Selezione e copia manuale del testo",
      "Screenshot del sistema operativo",
      "Foto dello schermo con dispositivo esterno",
      "Estrazione tramite strumenti per sviluppatori del browser",
    ],
  },
  L3: {
    label: "L3 — Anti-copia soft",
    short: "Massima protezione lato client",
    description:
      "Massimo livello tecnicamente realizzabile senza watermark dinamico né asset cifrati. Vengono disattivate selezione testo, menu contestuale, trascinamento immagini e le principali scorciatoie di copia/salvataggio/stampa. Resta tecnicamente impossibile bloccare screenshot del sistema operativo o foto dello schermo: per quelli serve la Fase 2 (watermark dinamico).",
    prevents: [
      "Tutto quanto previsto da L2",
      "Selezione del testo (user-select disabilitato)",
      "Menu contestuale (tasto destro)",
      "Trascinamento delle immagini fuori dal browser",
      "Scorciatoie Ctrl/Cmd+C, Ctrl/Cmd+S, Ctrl/Cmd+P",
    ],
    remains: [
      "Screenshot di sistema (PrintScreen, Cmd+Shift+4, ecc.)",
      "Foto dello schermo con cellulare o macchina fotografica",
      "Ispezione tramite strumenti per sviluppatori del browser",
      "Estrazione automatizzata con browser headless (mitigata da auth + rate limit lato server)",
    ],
  },
};

/** Default per nuovi tenant: livello L1 (autenticazione + RLS). */
export const DEFAULT_CONTENT_PROTECTION: ContentProtectionConfig = {
  level: "L1",
  legal_notice: null,
};

/**
 * Risolve i flag effettivi a partire dalla configurazione del tenant.
 * In Fase 1 non sono previsti override granulari: si usa il preset del livello.
 */
export function resolveProtectionFlags(
  config: Partial<ContentProtectionConfig> | null | undefined,
): ContentProtectionFlags {
  const level = (config?.level as ContentProtectionLevel) || DEFAULT_CONTENT_PROTECTION.level;
  return LEVEL_PRESETS[level] ?? LEVEL_PRESETS.L1;
}